<template>
	<div id="contact-import">
		<div v-if="importState === 'select-type'" class="select-contact-type">
			<FormDropdowns
				label="Please specify the category of Contact Type"
				name="contact-type"
				v-model="contactTypeId"
				:options="contactTypeOptions"
			/>
		</div>
		<div v-if="importState === 'upload' && oidcBearerToken">
			<div
				class="dropzone-container import-file"
				ref="dropzoneContainer"
				data-test="dropzone-container"
			>
				<div class="dropzone-message">
					<font-awesome-icon
						class="icon-styles dropzone-message-cloud"
						:icon="['fal', 'cloud-upload']"
					/>
					<p class="dropzone-message-filetypes">
						You may import an xls, xlsx, or xml file.
					</p>
				</div>
				<VueDropzone
					id="dropzone"
					class="dropzone"
					ref="dropzone"
					data-test="dropzone"
					:include-styling="false"
					:useCustomSlot="true"
					:options="dropZoneOptions"
					@vdropzone-complete="getMappings(...arguments)"
					@vdropzone-file-added="handleDropZoneFileAdded($event)"
					@vdropzone-removed-file="handleDropZoneFileRemoved($event)"
					@vdropzone-error="handleDropZoneError()"
					@vdropzone-mounted="handleDropZoneMounted()"
				>
					<div class="dropzone-content">
						<p>Drag and drop your file or</p>
						<button class="b360-btn b360-modal-action-btn">Browse</button>
					</div>
				</VueDropzone>
			</div>
		</div>

		<div v-else-if="importState === 'mapping'" data-test="mapping">
			Please make a selection for each of the categories before proceeding.
			<p v-if="requiredFieldError" class="required-field-error">
				All fields must be filled before proceeding.
			</p>
			<div class="contact-import-container">
				<div
					v-for="(field, findex) in mapping.mappings"
					:key="findex"
					v-show="field.description !== 'Import Date'"
					v-bind:class="
						addHorizontalLine({
							description: field.columnCategory.categoryDescription,
							index: findex,
						})
					"
				>
					<div class="b360-grid-row">
						<div
							class="b360-col-lg-12 b360-col-md-12 b360-col-sm-12 b360-col-xs-12 column-category-text"
							data-test="column-category-text"
						>
							{{
								displayCategory({
									description: field.columnCategory.categoryDescription,
								})
							}}
						</div>
					</div>
					<div class="b360-grid-row">
						<div
							class="b360-col-lg-5 b360-col-md-5 b360-col-sm-4 b360-col-xs-4 right-align"
						>
							<label v-show="field.isRequired === true" class="asterisk"
								>*</label
							>
							{{ field.description }}
							<label v-show="field.isRequired === true">(Required)</label>
						</div>
						<div
							class="b360-col-lg-7 b360-col-md-7 b360-col-sm-8 b360-col-xs-8"
						>
							<select
								class="b360-form-input contact-import-select"
								:id="`contact-import-select-${findex}`"
								:required="field.isRequired"
								@change="handleSelect(findex)"
							>
								<option value="-1">None</option>
								<option
									v-for="(option, index) in mapping.fileColumns"
									:value="index"
									:key="index"
									:selected="field.fileColumn === option.description"
								>
									{{ option.description }}
								</option>
							</select>
						</div>
					</div>
				</div>
			</div>
		</div>
		<div
			v-else-if="importState === 'incomplete'"
			class="save-mappings-incomplete"
		>
			<p>Due to its file size, your import is taking longer than expected.</p>
			<p>Please check back later to view your imported contacts.</p>
		</div>
		<div v-else-if="importState === 'complete'">
			<p v-if="resultCounts.SavedRecords > 0">
				You have successfully uploaded
				{{ resultCounts.SavedRecords }} of {{ resultCounts.total }} records.
			</p>
			<p class="asterisk" v-if="resultCounts.DuplicatedRecords > 0">
				{{ resultCounts.DuplicatedRecords }} of total
				{{ resultCounts.total }} records were not uploaded due to duplication.
			</p>
			<p class="asterisk" v-if="resultCounts.MissingRequiredFields > 0">
				{{ resultCounts.MissingRequiredFields }} of total
				{{ resultCounts.total }} records were not uploaded due to missing first
				name, last name and/or Borrower Email.
			</p>
			<p class="asterisk" v-if="resultCounts.FailedToSave > 0">
				{{ resultCounts.FailedToSave }} of total
				{{ resultCounts.total }} records were not uploaded due to an error.
			</p>
		</div>
		<div v-else-if="importState === 'error'" class="save-mappings-error">
			<p>Sorry, there was an error while importing.</p>
		</div>
	</div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import VueDropzone from 'vue2-dropzone';
import FormDropdowns from '@/components/global/FormDropdowns.vue';
import contactImport from '@/services/toolbox/contact-import-service';
import oidcManager from '@/services/auth/oidc-manager';
import partnerPointsApi from '@/services/Lopp/partner-points-service';

let currentCategory = '';

export default {
	name: 'MultipleContactsImport',
	components: {
		VueDropzone,
		FormDropdowns,
	},
	data() {
		return {
			contactTypeId: 1,
			importState: 'select-type',
			mapping: {},
			resultCounts: null,
			requiredFieldError: false,
			oidcBearerToken: null,
		};
	},
	computed: {
		...mapGetters(['currentUserRolodexIds', 'contactTypeOptions']),
		...mapState({
			contactId: (state) => state.profile.rolodexContactId,
			userId: (state) => state.profile.userId,
			isInternalUser: (state) =>
				state.profileSwitcher.profileSelector.isInternalUser,
		}),
		dropZoneOptions() {
			if (this.oidcBearerToken) {
				return {
					// eslint-disable-next-line no-undef
					url: `${globalConfig.api.endpoint_uri.replace(
						/\/$/,
						'',
						// eslint-disable-next-line no-undef
					)}/${globalConfig.api.version
						.replace(/^\//, '')
						.replace(/\/$/, '')}/ContactsImport/Upload`,
					maxFiles: 1,
					maxFilesize: 5,
					addRemoveLinks: true,
					autoProcessQueue: false,
					uploadMultiple: false,
					disablePreviews: true,
					previewsContainer: '.dz-message',
					acceptedFiles: '.xls, .xlsx, .xml',
					dictFileTooBig: 'Total file size can be no more than 5MB.',
					dictMaxFilesExceeded: 'Cannot upload more than one file at a time.',
					dictInvalidFileType: 'Cannot upload files of this type.',
					headers: {
						Authorization: `Bearer ${this.oidcBearerToken}`,
					},
				};
				// eslint-disable-next-line no-else-return
			} else {
				return null;
			}
		},
		contactTypeName() {
			const [{ name }] = this.contactTypeOptions.filter(
				(contactType) => contactType.id === this.contactTypeId,
			);
			return name;
		},
	},
	methods: {
		getSelectType() {
			this.importState = 'select-type';
			this.$emit('stateChange', { importState: this.importState });
		},
		getContactList() {
			let modalTitle;
			if (this.contactTypeName === 'Other') {
				modalTitle = 'Import Multiple Contacts';
			} else {
				modalTitle = `Import ${this.contactTypeName} Contacts`;
			}
			this.importState = 'upload';
			this.$emit('stateChange', { importState: this.importState, modalTitle });
		},
		getFiles() {
			if (this.$refs.dropzone.getAcceptedFiles().length) {
				this.$emit('waiting', true);
				this.$refs.dropzone.processQueue();
			} else {
				// eslint-disable-next-line no-alert
				alert('Please add a file to upload.');
			}
		},
		getMappings(data) {
			if (data.status !== 'error' && data.xhr) {
				if (data.xhr.response.length > 0) {
					this.mapping = JSON.parse(data.xhr.response);
					this.importState = 'mapping';
					this.$emit('stateChange', { importState: this.importState });
				}
				this.$emit('waiting', false);
			}
		},
		async saveMappings() {
			this.requiredFieldError = false;
			const mapArray = [
				...document.getElementsByClassName('contact-import-select'),
			];
			// eslint-disable-next-line no-restricted-syntax
			for (const map of mapArray) {
				const required = map.getAttribute('required') === 'required';
				if (required && map.selectedIndex === 0) {
					map.classList.add('error');
					this.requiredFieldError = true;
				} else {
					map.classList.remove('error');
				}
			}

			if (this.requiredFieldError) {
				document.querySelector('.global-modal').scrollTop = 0;
			} else {
				try {
					this.$emit('waiting', true);
					const { entityId } = this.currentUserRolodexIds;
					const result = await contactImport.saveContactsMappingv2({
						fileId: this.mapping.fileId,
						contentType: this.mapping.contentType,
						containerPath: this.mapping.containerPath,
						elementName: this.mapping.elementName,
						fileColumns: this.mapping.fileColumns,
						mappings: this.mapping.mappings,
						contactId: this.contactId,
						entityId: entityId,
						userId: this.userId,
						isInternalUser: this.isInternalUser,
						leadTypeId: this.contactTypeId,
					});
					if (result.data.status === 'Success') {
						this.resultCounts = result.data.resultCounts;
						this.resultCounts.total =
							this.resultCounts.SavedRecords +
							this.resultCounts.FailedToSave +
							this.resultCounts.DuplicatedRecords +
							this.resultCounts.MissingRequiredFields;
						if (result.data.resultCounts.SavedRecords > 0) {
							partnerPointsApi.contactsImportAddPointsAsync();
						}
						this.importState = 'complete';
					} else {
						this.importState = 'error';
					}
				} catch (error) {
					const isTimeoutError = error.message.includes('timeout');
					this.importState = isTimeoutError ? 'incomplete' : 'error';
				} finally {
					this.$emit('stateChange', { importState: this.importState });
					this.$emit('waiting', false);
				}
			}
		},
		handleSelect(index) {
			const elSelect = document.getElementById(
				`contact-import-select-${index}`,
			);
			const selectedIndex = elSelect.selectedIndex - 1;
			const selectedValue =
				selectedIndex >= 0
					? this.mapping.fileColumns[selectedIndex].description
					: null;
			const required = elSelect.getAttribute('required') === 'required';

			this.mapping.mappings[index].fileColumn = selectedValue;

			// handle validation error
			if (required) {
				if (selectedValue !== null) {
					elSelect.classList.remove('error');
				} else {
					elSelect.classList.add('error');
				}
			}
		},
		resetData() {
			this.importState = 'select-type';
			this.mapping = {};
			this.$emit('waiting', false);
		},
		handleDropZoneMounted() {
			this.$emit('dropZoneChange', { hasFile: false });
		},
		handleDropZoneFileRemoved() {
			// prevents and error logged to console if modal closed
			if (this.importState === 'upload') {
				this.$refs.dropzoneContainer.classList.add('import-file');
				this.$refs.dropzoneContainer.classList.remove('file-imported');
			}
			this.$emit('dropZoneChange', { hasFile: false });
		},
		handleDropZoneFileAdded(file) {
			this.$refs.dropzoneContainer.classList.add('file-imported');
			this.$refs.dropzoneContainer.classList.remove('import-file');
			const hasFile = file.status !== 'error';
			this.$emit('dropZoneChange', { hasFile });
		},
		handleDropZoneError() {
			if (this.$refs.dropzone.getAcceptedFiles().length === 0) {
				this.$emit('dropZoneChange', { hasFile: false });
			}
		},
		setContactType() {
			if (this.contactTypeName === 'Other') {
				return 'Contact Information';
			} else {
				return this.contactTypeName;
			}
		},
		// displayCategory({ categoryDescription: description }) {
		displayCategory({ description }) {
			let category = description;

			if (category === 'Borrower') {
				category = this.setContactType();
			}

			if (category !== currentCategory) {
				currentCategory = category;
			} else {
				category = '';
			}

			return category;
		},
		// eslint-disable-next-line consistent-return
		addHorizontalLine({ description, index }) {
			let category = description;

			if (category === 'Borrower') {
				category = this.setContactType();
			}

			if (index > 1 && currentCategory !== category && category !== '') {
				return 'horizontal-line';
			}
		},
	},
	mounted() {
		oidcManager.getUser().then((user) => {
			this.oidcBearerToken = user && user.access_token;
		});
	},
};
</script>

<style lang="scss" scoped>
.select-contact-type {
	margin: auto;
	display: flex;
	flex-direction: column;
	align-items: center;
}

.dropzone {
	margin: auto;
	padding: 50px 0;
	display: flex;
	justify-content: center;
	height: auto;
	max-width: 400px;
	text-align: center;
	cursor: pointer;
	border-color: black;
	border-style: dashed;
}

.dropzone-message-cloud {
	width: 100%;
	font-size: 5rem;
	color: $b360-color--blue;
}

.dropzone-message {
	display: flex;
	flex-direction: column;
	align-items: center;
	text-align: center;
}

.dropzone-content {
	display: flex;
	justify-content: center;
	align-items: center;
	// height: 150px;

	p {
		padding: 0;
	}
}

/* verbose because of global styling */
p:not(.description).required-field-error {
	@include rem(margin-top, 16px);
	color: $b360-color--red__error;
	clear: left;
}

.contact-import-container {
	@include rem(padding, 20px 100px 0 100px);

	@media (max-width: 1400px) {
		@include rem(padding, 20px 0 0 0);
	}

	padding-left: 3em;
	padding-right: 3em;
}

.contact-import-container > div {
	margin-top: -10px;
}

.contact-import-select {
	@include rem(height, 44px);
	padding: 0.6rem 0.75rem;
	&.error {
		border: 1px solid $error;
	}

	option {
		@media (max-width: 1400px) {
			@include rem(font-size, 16px);
		}
	}
}

.horizontal-line {
	padding-top: 0.5em;
	margin-top: 10px !important;
	border-top-style: solid;
	border-top-width: 1px;
	border-color: lightgray;
}

.asterisk {
	color: $b360-color--red__error;
}

.column-category-text {
	font-weight: bold;
	padding-bottom: 10px;
	font-size: large;
}

.right-align {
	text-align: right;
}

.save-mappings-incomplete {
	text-align: center;
	p:last-child {
		padding-bottom: 0;
	}
}

.save-mappings-error p {
	color: $b360-color--red__error;
}

.file-imported {
	.dropzone-message-cloud,
	.dropzone-message-filetypes,
	.dropzone-content {
		display: none;
	}

	.dropzone {
		padding: 0;
		border-width: 0;
	}
}
</style>

<style lang="scss">
.dz-success-mark,
.dz-error-mark {
	display: none;
}

.dz-error {
	.dz-size,
	.dz-filename,
	.dz-remove {
		display: none;
	}
}

.file-imported {
	.dz-success-mark,
	.dz-error-mark {
		display: none;
	}

	.dz-remove {
		line-height: 50px;
	}
}
.dz-message .dz-error:not(:last-of-type) {
	display: none;
}
</style>
